import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import { Plus, Search, Printer } from 'lucide-react';

const PackingList = () => {
  const [lists, setLists] = useState([]);
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [companySettings, setCompanySettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);

  const emptyForm = {
    sales_order_id: '', customer_id: '', customer_name: '', customer_address: '',
    product_name: '', product_size: '', batch_number: '', total_quantity: '', date: '',
    items: [], box_rows: [{ box_number: 1, bundles: '', pieces_per_bundle: '', total_qty: '', net_weight: '', gross_weight: '' }]
  };
  const [formData, setFormData] = useState({ ...emptyForm });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [listsRes, ordersRes, custRes, invRes, settingsRes] = await Promise.all([
        api.get('/sales/packing-lists'), api.get('/sales/orders'),
        api.get('/purchase/customers'), api.get('/sales/inventory'),
        api.get('/sales/company-settings')
      ]);
      setLists(listsRes.data); setOrders(ordersRes.data);
      setCustomers(custRes.data); setInventory(invRes.data);
      setCompanySettings(settingsRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const selectOrder = (orderId) => {
    const order = orders.find(o => o.id === orderId);
    if (order) {
      setSelectedOrder(order);
      const cust = customers.find(c => c.name === order.customer_name);
      setFormData(prev => ({
        ...prev,
        sales_order_id: orderId,
        customer_id: cust?.id || '',
        customer_name: order.customer_name,
        customer_address: cust ? `${cust.name}\n${cust.address || ''}`.trim() : order.customer_name,
        items: order.items.map(item => ({ sku: item.sku, name: item.name, ordered_qty: item.quantity, pack_qty: item.quantity, box_number: '' }))
      }));
    }
  };

  const addBoxRow = () => {
    setFormData(prev => ({
      ...prev,
      box_rows: [...prev.box_rows, { box_number: prev.box_rows.length + 1, bundles: '', pieces_per_bundle: '', total_qty: '', net_weight: '', gross_weight: '' }]
    }));
  };
  const updateBoxRow = (idx, field, val) => {
    setFormData(prev => {
      const rows = [...prev.box_rows];
      rows[idx] = { ...rows[idx], [field]: val };
      return { ...prev, box_rows: rows };
    });
  };
  const removeBoxRow = (idx) => {
    setFormData(prev => ({
      ...prev,
      box_rows: prev.box_rows.filter((_, i) => i !== idx).map((r, i) => ({ ...r, box_number: i + 1 }))
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/sales/packing-lists', formData);
      setShowModal(false); setSelectedOrder(null); setFormData({ ...emptyForm }); fetchData();
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const printPacking = (list) => {
    const co = companySettings;
    const boxRows = (list.box_rows || []).map(r =>
      `<tr>
        <td style="text-align:center;border:1px solid #000;padding:6px">${r.box_number || ''}</td>
        <td style="text-align:center;border:1px solid #000;padding:6px">${r.bundles || ''}</td>
        <td style="text-align:center;border:1px solid #000;padding:6px">${r.pieces_per_bundle || ''}</td>
        <td style="text-align:center;border:1px solid #000;padding:6px">${r.total_qty || ''}</td>
        <td style="text-align:center;border:1px solid #000;padding:6px">${r.net_weight || ''}</td>
        <td style="text-align:center;border:1px solid #000;padding:6px">${r.gross_weight || ''}</td>
      </tr>`
    ).join('');

    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>Packing List - ${list.packing_number}</title>
    <style>
      @page{size:A4;margin:15mm}body{font-family:'Arial',sans-serif;font-size:12px;margin:0;padding:20px;color:#000}
      .header{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:3px solid #000;padding-bottom:10px;margin-bottom:10px}
      .company-name{font-size:22px;font-weight:bold;color:#1a237e}
      .company-info{text-align:right;font-size:10px;line-height:1.5}
      .doc-title{text-align:center;font-size:18px;font-weight:bold;margin:12px 0;text-decoration:underline}
      .info-grid{display:flex;justify-content:space-between;margin-bottom:16px}
      .info-left{width:60%}.info-right{width:35%;text-align:right}
      .info-left p,.info-right p{margin:3px 0;font-size:11px}
      table{width:100%;border-collapse:collapse}
      th{background:#1a237e;color:#fff;padding:8px;border:1px solid #000;font-size:10px}
      @media print{button{display:none !important}}
    </style></head><body>
    <div class="header">
      <div><img src="https://server.dweb.co.in/img/cpp.png" alt="logo" style="width:15%; margin:auto;" /></div>
      <div class="company-info">${co.address || ''}<br/>${co.email || ''}<br/>${co.website || ''}</div>
    </div>
    <div class="doc-title">PACKING LIST</div>
    <div class="info-grid">
      <div class="info-left">
        <p><strong>TO,</strong></p>
        <p style="white-space:pre-line">${list.customer_name || ''}\n${list.customer_address || ''}</p>
        <p style="margin-top:10px"><strong>Sub:</strong> Packing List - Reg.</p>
        <p style="margin-top:8px"><strong>Name of the Product:</strong> ${list.product_name || ''}</p>
        <p><strong>Size:</strong> ${list.product_size || ''}</p>
        <p><strong>Qty:</strong> ${list.total_quantity || ''}</p>
      </div>
      <div class="info-right">
        <p><strong>Date:</strong> ${list.date || new Date(list.packed_at).toLocaleDateString('en-GB')}</p>
        <p><strong>Batch No:</strong> ${list.batch_number || ''}</p>
      </div>
    </div>
    <h3 style="text-align:center;font-size:13px;margin:16px 0 8px">QUANTITY PER EACH BOX</h3>
    <table>
      <thead><tr>
        <th>Box Number</th><th>Bundles in each box (A)</th><th>No. of pieces per bundle (B)</th>
        <th>Total qty (A)*(B)</th><th>Total Net Weight (Kg)</th><th>Total Gross Weight (Kg)</th>
      </tr></thead>
      <tbody>${boxRows}</tbody>
    </table>
    <button onclick="window.print()" style="margin-top:20px;padding:8px 24px;cursor:pointer">Print</button>
    </body></html>`);
    w.document.close();
  };

  const filtered = lists.filter(l =>
    (l.packing_number||'').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (l.customer_name||'').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (l.product_name||'').toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div data-testid="packing-page">
      <div className="flex items-center justify-between mb-4">
        <h1 style={{ fontSize: '24px', fontWeight: 700, margin: 0 }}>Packing Lists</h1>
        <button onClick={() => { setFormData({ ...emptyForm }); setSelectedOrder(null); setShowModal(true); }} className="btn btn-primary" data-testid="create-packing-btn"><Plus size={16} /> Create Packing List</button>
      </div>

      <div className="card mb-4">
        <div className="form-group" style={{ marginBottom: 0, position: 'relative' }}>
          <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)' }} />
          <input type="text" className="form-input" placeholder="Search by packing #, customer, product..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} style={{ paddingLeft: '40px' }} data-testid="search-input" />
        </div>
      </div>

      <div className="card">
        {loading ? <div data-testid="loading-indicator">Loading...</div> : (
          <div className="table-container">
            <table>
              <thead><tr><th>Packing #</th><th>Customer</th><th>Product</th><th>Batch</th><th>Boxes</th><th>Date</th><th>Actions</th></tr></thead>
              <tbody>
                {filtered.length === 0 ? <tr><td colSpan={7} style={{ textAlign: 'center', padding: '20px', color: 'var(--text-tertiary)' }}>No packing lists</td></tr> : filtered.map(list => (
                  <tr key={list.id} data-testid={`packing-row-${list.packing_number}`}>
                    <td className="font-mono">{list.packing_number}</td>
                    <td>{list.customer_name || '-'}</td>
                    <td>{list.product_name || '-'}</td>
                    <td>{list.batch_number || '-'}</td>
                    <td className="tabular-nums">{list.box_rows?.length || 0}</td>
                    <td className="text-sm">{list.date || new Date(list.packed_at).toLocaleDateString()}</td>
                    <td><button onClick={() => printPacking(list)} className="btn btn-ghost btn-sm" title="Print" data-testid={`print-btn-${list.packing_number}`}><Printer size={14} /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" data-testid="packing-modal">
          <div className="modal-content" style={{ maxWidth: '850px', maxHeight: '90vh', overflow: 'auto' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>Create Packing List</h2></div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="grid grid-cols-2" style={{ gap: '12px' }}>
                  <div className="form-group">
                    <label className="form-label">Sales Order *</label>
                    <select className="form-select" value={formData.sales_order_id} onChange={(e) => selectOrder(e.target.value)} required data-testid="order-select">
                      <option value="">Select...</option>
                      {orders.filter(o => o.status !== 'cancelled').map(o => <option key={o.id} value={o.id}>{o.order_number} - {o.customer_name}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Date</label>
                    <input type="text" className="form-input" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} placeholder="DD/MM/YYYY" data-testid="date-input" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Product Name</label>
                    <input type="text" className="form-input" value={formData.product_name} onChange={(e) => setFormData({ ...formData, product_name: e.target.value })} data-testid="product-name" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Size</label>
                    <input type="text" className="form-input" value={formData.product_size} onChange={(e) => setFormData({ ...formData, product_size: e.target.value })} placeholder='e.g. 21" x 32"' data-testid="product-size" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Total Quantity</label>
                    <input type="text" className="form-input" value={formData.total_quantity} onChange={(e) => setFormData({ ...formData, total_quantity: e.target.value })} placeholder="e.g. 2026.52 kgs / 14,700 nos" data-testid="total-qty" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Batch No</label>
                    <input type="text" className="form-input" value={formData.batch_number} onChange={(e) => setFormData({ ...formData, batch_number: e.target.value })} data-testid="batch-input" />
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '20px 0 10px' }}>
                  <h3 style={{ fontSize: '14px', fontWeight: 700, margin: 0 }}>Quantity Per Each Box</h3>
                  <button type="button" onClick={addBoxRow} className="btn btn-secondary btn-sm" data-testid="add-box-row"><Plus size={12} /> Add Box</button>
                </div>
                <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
                  <thead><tr>
                    <th style={{ padding: '6px', width: '60px' }}>Box #</th>
                    <th style={{ padding: '6px' }}>Bundles (A)</th>
                    <th style={{ padding: '6px' }}>Pieces/Bundle (B)</th>
                    <th style={{ padding: '6px' }}>Total Qty (AxB)</th>
                    <th style={{ padding: '6px' }}>Net Wt (Kg)</th>
                    <th style={{ padding: '6px' }}>Gross Wt (Kg)</th>
                    <th style={{ padding: '6px', width: '40px' }}></th>
                  </tr></thead>
                  <tbody>
                    {formData.box_rows.map((row, idx) => (
                      <tr key={idx}>
                        <td style={{ padding: '4px', textAlign: 'center' }}>{row.box_number}</td>
                        <td style={{ padding: '4px' }}><input type="text" className="form-input" value={row.bundles} onChange={(e) => updateBoxRow(idx, 'bundles', e.target.value)} style={{ fontSize: '12px', padding: '4px' }} /></td>
                        <td style={{ padding: '4px' }}><input type="text" className="form-input" value={row.pieces_per_bundle} onChange={(e) => updateBoxRow(idx, 'pieces_per_bundle', e.target.value)} style={{ fontSize: '12px', padding: '4px' }} /></td>
                        <td style={{ padding: '4px' }}><input type="text" className="form-input" value={row.total_qty} onChange={(e) => updateBoxRow(idx, 'total_qty', e.target.value)} style={{ fontSize: '12px', padding: '4px' }} /></td>
                        <td style={{ padding: '4px' }}><input type="text" className="form-input" value={row.net_weight} onChange={(e) => updateBoxRow(idx, 'net_weight', e.target.value)} style={{ fontSize: '12px', padding: '4px' }} /></td>
                        <td style={{ padding: '4px' }}><input type="text" className="form-input" value={row.gross_weight} onChange={(e) => updateBoxRow(idx, 'gross_weight', e.target.value)} style={{ fontSize: '12px', padding: '4px' }} /></td>
                        <td style={{ padding: '4px' }}>{formData.box_rows.length > 1 && <button type="button" onClick={() => removeBoxRow(idx)} className="btn btn-ghost btn-sm" style={{ color: 'red' }}>x</button>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="modal-footer">
                <button type="button" onClick={() => setShowModal(false)} className="btn btn-secondary">Cancel</button>
                <button type="submit" className="btn btn-primary" data-testid="submit-packing-btn">Create Packing List</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PackingList;
